import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  selectServiceReport,
  getServiceReport,
} from "../../customerMachineServiceReportsSlice";
import { CustomerMachineState } from "../../models/models";
import { ServiceReportsState } from "../../models/ServiceReportsModel";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import publicServiceReportsApi from "../../../../public/api/publicServiceReportsApi";
import useTranslation from "../../../../shared/hooks/useTranslation";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import accountContext from "../../../../account/accountContext";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { dt } from "../../utils/dt";
import ServiceReportDetailView from "./ServiceReportDetailView";
import ServiceReportDialog from "../ServiceReportDialog";
import SolvingServiceReportDialog from "../SolvingServiceReportDialog";
import ServiceReportShareDialog from "../ServiceReportShareDialog";

// ===================================================================
//  KEYFRAMES (injected once via style tag)
// ===================================================================
const DETAIL_KEYFRAMES_ID = "sr-detail-keyframes";
const DETAIL_KEYFRAMES = `
  @keyframes heroReveal {
    0%   { opacity: 0; transform: translateY(-12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes sectionReveal {
    0%   { opacity: 0; transform: translateY(16px) scale(0.98); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

// ===================================================================
//  THIN WRAPPER — provides page chrome, breadcrumb, dialogs
// ===================================================================
const SrDetailPage = () => {
  useRedesignSetup();

  const { id: machineId, reportNumber } = useParams<{
    id: string;
    reportNumber: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");

  // Permission checks for action bar
  const { canWrite } = usePermission(Modules.Machinery);
  const currentUserId = accountContext.user?.id;

  const { machine } = useAppSelector<CustomerMachineState>(
    (state: any) => state.customerMachinery
  );
  const { serviceReport } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [solveDialogOpen, setSolveDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Inject page-specific keyframes
  useEffect(() => {
    if (!document.getElementById(DETAIL_KEYFRAMES_ID)) {
      const style = document.createElement("style");
      style.id = DETAIL_KEYFRAMES_ID;
      style.textContent = DETAIL_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  // Permission-based action visibility
  const isOwnReport = !!currentUserId && serviceReport?.createdByUserId === currentUserId;
  const canEdit = canWrite || isOwnReport;
  const canSolve = canWrite || isOwnReport;
  const canCreateSR = canWrite;

  const displayName = machine ? getMachineDisplayName(machine) : "";

  // Action handlers
  const handleDownloadPdf = useCallback(() => {
    if (serviceReport) {
      publicServiceReportsApi.getServiceReportPdfLink(
        serviceReport.number,
        `service-report-${serviceReport.number}.pdf`
      );
    }
  }, [serviceReport]);

  const handleDialogClose = useCallback(() => {
    setEditDialogOpen(false);
    setSolveDialogOpen(false);
    setShareDialogOpen(false);
    if (reportNumber) {
      dispatch(getServiceReport(reportNumber));
    }
  }, [reportNumber]);

  const handleCreateServiceRequest = useCallback(() => {
    if (serviceReport && machineId) {
      navigate(
        `/service-requests/new?machine=${machineId}&report=${serviceReport.number}`
      );
    }
  }, [serviceReport, machineId]);

  const handleEdit = useCallback(() => {
    if (serviceReport) {
      dispatch(selectServiceReport(serviceReport));
      setEditDialogOpen(true);
    }
  }, [serviceReport]);

  const handleSolve = useCallback(() => {
    if (serviceReport) {
      dispatch(selectServiceReport(serviceReport));
      setSolveDialogOpen(true);
    }
  }, [serviceReport]);

  const handleBack = useCallback(() => {
    navigate(machineId ? `/machinery/${machineId}` : "/machinery");
  }, [machineId]);

  // ===================================================================
  //  BREADCRUMB
  // ===================================================================
  const breadcrumb = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        mb: "16px",
        flexWrap: "wrap",
        animation: "heroReveal 0.4s ease-out both",
        maxWidth: 1280,
        margin: "0 auto",
        px: { xs: 1.5, md: 3 },
        pt: { xs: 1.5, md: 2.5 },
      }}
    >
      <BreadcrumbLink
        label={translate("breadcrumbEquipment")}
        onClick={() => navigate("/machinery")}
      />
      <BreadcrumbSep />
      <BreadcrumbLink
        label={displayName || translate("machine")}
        onClick={() => machineId && navigate(`/machinery/${machineId}`)}
        truncate
      />
      <BreadcrumbSep />
      <BreadcrumbLink
        label={translate("serviceReportsTab")}
        onClick={() => machineId && navigate(`/machinery/${machineId}`)}
      />
      <BreadcrumbSep />
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          fontFamily: dt.fontMono,
          color: dt.profile.breadcrumb.currentColor,
        }}
      >
        #{serviceReport?.number || reportNumber}
      </Box>
    </Box>
  );

  // ===================================================================
  //  RENDER
  // ===================================================================
  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        minHeight: "calc(100vh - 64px)",
        background: dt.pageBg,
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        backgroundImage: `radial-gradient(${dt.profile.dotGrid.color} 1px, transparent 1px)`,
        backgroundSize: dt.profile.dotGrid.size,
      }}
    >
      {breadcrumb}

      <ServiceReportDetailView
        machineId={machineId || ""}
        reportNumber={reportNumber || ""}
        compact={false}
        onEdit={handleEdit}
        onSolve={handleSolve}
        onShare={() => setShareDialogOpen(true)}
        onDownloadPdf={handleDownloadPdf}
        onCreateServiceRequest={handleCreateServiceRequest}
        onBack={handleBack}
        canEdit={canEdit}
        canSolve={canSolve}
        canCreateSR={canCreateSR}
      />

      {/* Dialogs */}
      {editDialogOpen && (
        <ServiceReportDialog
          open={editDialogOpen}
          onClose={handleDialogClose}
          machineId={machineId || ""}
        />
      )}

      {solveDialogOpen && (
        <SolvingServiceReportDialog
          open={solveDialogOpen}
          onClose={handleDialogClose}
          machineId={machineId || ""}
        />
      )}

      {serviceReport && (
        <ServiceReportShareDialog
          open={shareDialogOpen}
          serviceReport={serviceReport}
          onClose={() => setShareDialogOpen(false)}
        />
      )}
    </Box>
  );
};

// ===================================================================
//  BREADCRUMB HELPERS
// ===================================================================
const BreadcrumbLink = ({
  label,
  onClick,
  truncate,
}: {
  label: string;
  onClick?: () => void;
  truncate?: boolean;
}) => (
  <Box
    onClick={onClick}
    sx={{
      fontSize: "14px",
      fontWeight: 500,
      fontFamily: dt.font,
      color: dt.profile.breadcrumb.linkColor,
      cursor: "pointer",
      "&:hover": { textDecoration: "underline" },
      ...(truncate && {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: { xs: "120px", md: "200px" },
      }),
    }}
  >
    {label}
  </Box>
);

const BreadcrumbSep = () => (
  <Box
    component="span"
    sx={{
      fontSize: "16px",
      color: dt.profile.breadcrumb.separator,
      userSelect: "none",
    }}
  >
    /
  </Box>
);

export default SrDetailPage;
